import { useMemo } from 'react';
import { MessagePrimitive, useAssistantState } from '@assistant-ui/react';
import clsx from 'clsx';

import { AIChatToolTypes } from '@atom/types/ai';

import ChatAssetDraftTool from './ChatAssetDraftTool/ChatAssetDraftTool';
import { useChatStatus } from './hooks/useChatStatus';
import { useIsAssistantMessageLoading } from './hooks/useIsAssitantMessageLoading';
import { useSuspendedPicker } from './hooks/useSuspendedPicker';
import ChatAssistantMessageFooter from './ChatAssistantMessageFooter';
import { useChatContext } from './ChatContext';
import ChatConversationalAnalyticsTool from './ChatConversationalAnalyticsTool';
import ChatIcon from './ChatIcon';
import ChatMarkdown from './ChatMarkdown';
import ChatSuspendedPicker from './ChatSuspendedPicker';

import classes from './chat.module.css';

const groupPartsTextFirst = (parts: readonly any[]) => {
  const textIndices: number[] = [];
  const toolIndices: number[] = [];

  parts.forEach((part, index) => {
    if (part.type === 'text') {
      textIndices.push(index);
    } else if (part.type === 'tool-call') {
      toolIndices.push(index);
    }
  });

  return [
    ...(textIndices.length > 0
      ? [{ groupKey: 'text', indices: textIndices }]
      : []),
    ...(toolIndices.length > 0
      ? [{ groupKey: 'tools', indices: toolIndices }]
      : []),
  ];
};

const ChatAssistantMessage = () => {
  const message = useAssistantState(state => state.message);
  const thread = useAssistantState(state => state.thread);
  const loading = useIsAssistantMessageLoading();
  const status = useChatStatus();

  const {
    ui: { chatOpen, chatExpanded },
  } = useChatContext();

  const isSidebarMode = chatOpen && !chatExpanded;

  const hasTextContent = useMemo(
    () =>
      message.content.some(
        part => part.type === 'text' && part.text.trim().length > 0,
      ),
    [message.content],
  );

  const hasToolContent = useMemo(
    () =>
      message.content.some(
        part => (part as { type: string }).type === 'tool-call',
      ),
    [message.content],
  );

  const { suspended } = useSuspendedPicker();

  if (!loading && !hasTextContent && !hasToolContent && !suspended) {
    return null;
  }

  return (
    <>
      <div
        className={clsx(
          classes.assistantMessageContainer,
          isSidebarMode && classes.sidebar,
        )}
      >
        <div className={classes.assistantMessageIcon}>
          <ChatIcon animated={message.isLast && loading} />
        </div>
        <div className={classes.assistantMessageContent}>
          {loading && status && !hasTextContent && (
            <div className={classes.statusIndicator}>{`${status}...`}</div>
          )}
          <MessagePrimitive.Unstable_PartsGrouped
            groupingFunction={groupPartsTextFirst}
            components={{
              Text: ChatMarkdown,
              tools: {
                by_name: {
                  [AIChatToolTypes.QUERY_CONVERSATIONAL_ANALYTICS]:
                    ChatConversationalAnalyticsTool,
                  [AIChatToolTypes.ASSET_DRAFT_FROM_PHOTO]: ChatAssetDraftTool,
                },
                Fallback: () => null,
              },
            }}
          />
          <ChatSuspendedPicker />
          {(!message.isLast || !thread.isRunning) && (
            <ChatAssistantMessageFooter />
          )}
        </div>
      </div>
    </>
  );
};

export default ChatAssistantMessage;
